import { FeathersError } from '@feathersjs/errors';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { ContractDocumentTypes } from '@tymbe/schema/enums';
import { Navigate } from 'react-router-dom';

import feathersClient from '../../../../apiClient';
import { getPaginatedResponse } from '../../../../apiClient/utils';
import { SuccessAlert } from '../../../../components/alerts';
import ErrorButton from '../../../../components/buttons/ErrorButton';
import { ExclamationCircleOutlined } from '../../../../components/icons';
import MushroomCloud from '../../../../components/icons/MushroomCloud';
import ModalShell from '../../../../components/modals/ModalShell';
import { InlineSpinner } from '../../../../utils/spinner';
import { PrimaryButton, SecondaryButton } from '@tymbe/ui-primitives';

export interface RemoveRegistrationButtonProps {
  personId: number;
}

const RemoveRegistrationButton = ({ personId }: RemoveRegistrationButtonProps) => {
  const [modalShown, setModalShown] = useState(false);
  const { mutateAsync: requestDeletePerson, isLoading, error, isSuccess } = useMutation<any, FeathersError>(
    ['hardDeletePerson', personId],
    () => feathersClient.service('person').remove(personId, { query: { $removeRegistration: true } }),
  );

  const { data: isRemovable } = useQuery(['isRegistrationRemovable', personId], async () => {
    const [applications, contracts] = await Promise.all([
      feathersClient.service('application').find({ query: { person_id: personId, $limit: 1 } }),
      feathersClient.service('person-document').find({
        query: {
          person_id: personId,
          $joinRelation: 'documentType',
          'documentType.type': { $in: ContractDocumentTypes },
          $limit: 1,
        },
      }),
    ]);
    return getPaginatedResponse(applications).total === 0 && getPaginatedResponse(contracts).total === 0;
  });

  const showModal = () => setModalShown(true);
  const hideModal = () => setModalShown(false);

  const removePerson = async () => {
    await requestDeletePerson();
    SuccessAlert('Osoba byla úspěšně smazána.');
  };

  if (isSuccess) {
    return <Navigate to="/tymber" />;
  }

  if (!isRemovable) {
    return null;
  }

  return (
    <>
      <ErrorButton
        onClick={showModal}
      >
        <MushroomCloud className="w-5" />
      </ErrorButton>
      <ModalShell
        showModal={modalShown}
        onClose={hideModal}
      >
        <div className="flex flex-col flex-grow gap-2">
          <h3><ExclamationCircleOutlined className="w-[22px] mr-1 text-danger inline" />Tato operace je nevratná!</h3>
          <p>Opravdu chcete trvale smazat tuto osobu?</p>
          {/* TODO: Extract to own component */}
          {error && <div className="text-error">Nepodařilo se smazat osobu.</div>}
          {error && <div className="text-error text-xs">{error.message}</div>}
          <div className="flex flex-row pt-8 flex-grow justify-end gap-3">
            {isLoading && <InlineSpinner />}
            {!isLoading && (
              <>
                <SecondaryButton
                  type="button"
                  variant='outlined'
                  onClick={hideModal}
                >
                  Zrušit
                </SecondaryButton>
                <PrimaryButton
                  type="button"
                  onClick={removePerson}
                >
                  Smazat
                </PrimaryButton>
              </>
            )}
          </div>
        </div>
      </ModalShell>
    </>
  );
};

export default RemoveRegistrationButton;
